import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MaxLoanDeleteService } from './max-loan-delete.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-max-loan-delete',
    templateUrl: './max-loan-delete.component.html',
    styleUrls: ['./max-loan-delete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MaxLoanDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};
  globalParams: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};

  private isInitialized = false;

  modalTitle = 'Delete Override Amount';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      const data = typeof rowData === 'string' ? safeJsonInput<any>(rowData || '{}', null, 'max-loan-delete.parse1') : rowData || {};
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        this.rowData = data;
      }
    } catch (e) { /* not yet stable */ }
  }

  @Input('header-data')
  set _headerData(headerData: string) {
    try {
      const h = typeof headerData === 'string' ? safeJsonInput<any>(headerData || '{}', null, 'max-loan-delete.parse2') : headerData || {};
      if (h && Object.keys(h).length > 0) {
        this.globalParams = h.globalParams || h.globalparams || h;
        const gc = h.generalConfigData || {};
        this.GENERAL_CONFIG = { base_url: gc.base_url || gc.api || '' };
        this.filterParams = h.filterParams || {};
      }
    } catch (e) { /* not yet stable */ }
  }

  constructor(
    private maxLoanDeleteService: MaxLoanDeleteService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    if (gc?.base_url) {
      this.GENERAL_CONFIG = gc;
    } else if (!this.GENERAL_CONFIG?.base_url) {
      this.GENERAL_CONFIG = { base_url: gc?.api || gc?.base_url || '' };
    }
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || this.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen) || this.filterParams || {};

    const activeData = activeScreen?.data || {};
    const bridgedRow = activeData?.selected?.rowData ?? activeData?.rowData;
    if (bridgedRow && typeof bridgedRow === 'object' && Object.keys(bridgedRow).length > 0) {
      this.rowData = bridgedRow;
    } else {
      const fromScreen = rowPayloadFromActiveScreen(activeScreen);
      if (fromScreen && Object.keys(fromScreen).length > 0) {
        this.rowData = fromScreen;
      }
    }
  }

  init() {
    if (this.isInitialized) return;
    try {
      if (!this.GENERAL_CONFIG?.base_url || !this.rowData || !Object.keys(this.rowData).length) {
        return;
      }
      this.isInitialized = true;
    } catch (e) { /* guard error */ }
  }

  deleteMaxLoanOverride() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build JSON object exactly like AngularJS controller
    const jsonSettings = '{}';
    const jsonObj = [];
    const tempObj = {
      'COMBINATION_KEY': this.rowData.COMBINATION_KEY,
      'TRANS_DETAILS_KEY': (this.rowData.TRANS_DETAILS_KEY == undefined || this.rowData.TRANS_DETAILS_KEY === 0) ? 0 : this.rowData.TRANS_DETAILS_KEY,
      'TRANS_HEADER_KEY': (this.rowData.TRANS_HEADER_KEY == undefined || this.rowData.TRANS_HEADER_KEY === 0) ? 0 : this.rowData.TRANS_HEADER_KEY,
      'ACCT_KEY': this.rowData.LOCAL_ACCT_KEY,
      'TP_KEY': this.rowData.TP_KEY,
      'CHART_KEY': this.rowData.LOCAL_CHART_KEY,
      'ISSUE_KEY': this.rowData.ISSUE_KEY
    };
    jsonObj[0] = tempObj;

    // Call service to delete
    this.maxLoanDeleteService.deleteMaxLoanOverride(
      this.GENERAL_CONFIG.base_url,
      jsonSettings,
      jsonObj,
        { globalParams: this.globalParams },
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.detectChanges();

        if (response.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar('Overridden value has been deleted successfully', 'gtw-snackbar--green');
          
          // Emit refresh event to update grid and close modal
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error occurred during deletion', 'gtw-snackbar--red');
          
          // Emit to close modal even on error
          this.requestGridUpdate.emit('cancel');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
        
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        
        // Emit cancel to close modal
        this.requestGridUpdate.emit('cancel');
      }
    );
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }
}

